/**
 * 메일 「환경설정 > 기본환경」의 값 한 벌 (migration 158, 2026-09-05).
 *
 * 화면(MailSettingsBasic)과 소비처(MailPage · MailDetail · ComposeModal)가 **같은 표**를 본다.
 * 서버 DTO 의 `mailXxx` 이름을 여기서 한 번만 벗겨, 화면 코드가 긴 이름을 되풀이하지 않는다.
 *
 * ★ **기본값이 곧 지금까지의 동작이다.** 서버가 이 키들을 모르거나(구 서버) 요청이 실패하면
 *   기본값으로 떨어져, 설정이 없던 때와 똑같이 동작한다.
 * ★ 밀도(`listDensity`)만은 **기기에도 적어 둔다**(mailView) — 목록이 뜨자마자 필요한 값이라
 *   서버 응답을 기다리면 좁게 그렸다가 넓어지는 것이 보인다. 서버가 진실이고 기기는 사본이다.
 */

import type {
  MailAddrDisplay, MailComposeWindow, MailEncoding, MailListDensity,
  MailRecipientInput, MailVcard, UserSettingsDto,
} from '../../api/users';
import type { MailI18nKey } from '../../lib/mail-i18n';

export interface MailBasicSettings {
  /* ── 메일 읽기 설정 ── */
  /**
   * migration 110 의 네 칸(firstFolder · pageSize · simplifySubject · quoteDivider)은
   * **개인설정 > 메일**에 있던 것을 2026-09-06 에 여기로 옮긴 것이다(사용자 지시).
   * 값·서버 라우트는 그대로다 — 고치는 자리만 하나로 모았다.
   */
  firstFolder: MailFirstFolder;
  pageSize: MailPageSize;
  simplifySubject: boolean;
  addrDisplay: MailAddrDisplay;
  blockImages: boolean;
  /** ★ 늘 켜져 있다 — 화면이 잠근 체크박스로 그린다(정본 P3 「할 수 없는 것은 잠근다」). */
  blockUnsafeTags: boolean;
  showOrigin: boolean;
  searchAll: boolean;
  listShowCc: boolean;
  listDensity: MailListDensity;
  /* ── 메일 쓰기 설정 ── */
  recipientInput: MailRecipientInput;
  sendConfirm: boolean;
  saveSent: boolean;
  readReceipt: boolean;
  /** 「편집기선택」 — 새 칸이 아니라 migration 110 의 `mailComposeMode` 다. */
  composeMode: 'HTML' | 'TEXT' | 'MARKDOWN';
  composeWindow: MailComposeWindow;
  encoding: MailEncoding;
  draftAlert: boolean;
  senderName: string;
  replyTo: string;
  vcardAttach: boolean;
  vcard: MailVcard | null;
  /** 답장·전달 본문 앞에 「──── 원본 메일 ────」 줄을 넣을지(migration 110). */
  quoteDivider: boolean;
}

/** 로그인 뒤 메일을 열면 먼저 보이는 메일함 (migration 110). */
export type MailFirstFolder = 'inbox' | 'star' | 'sent' | 'draft';
/** 목록 한 쪽에 몇 줄 (migration 110). 서버 enum 과 **같은 값**이어야 한다. */
export type MailPageSize = 15 | 30 | 50 | 100;

export const MAIL_FIRST_FOLDERS: MailFirstFolder[] = ['inbox', 'star', 'sent', 'draft'];
export const MAIL_PAGE_SIZES: MailPageSize[] = [15, 30, 50, 100];

export const MAIL_BASIC_DEFAULTS: MailBasicSettings = {
  // migration 110 의 기본값 그대로 — 옮겨 오면서 값이 달라지면 안 된다.
  firstFolder: 'inbox',
  pageSize: 50,
  simplifySubject: false,
  quoteDivider: true,
  addrDisplay: 'NAME_EMAIL',
  blockImages: false,
  blockUnsafeTags: true,
  showOrigin: false,
  searchAll: false,
  listShowCc: false,
  listDensity: 'NARROW',
  recipientInput: 'AUTOCOMPLETE',
  sendConfirm: true,
  saveSent: true,
  readReceipt: true,
  // ★ migration 158 이 새 행의 기본값을 'HTML' 로 바꿨다(사용자 지시 「기본은 html」).
  //   `mailPrefs.MAIL_PREF_DEFAULTS.composeMode` 는 'TEXT' 로 남는다 — 그것은 «서버가 못
  //   대답할 때» 쓰는 값이고, 이것은 «새 행의 기본값»이다. 뜻이 다르니 값도 다르다.
  composeMode: 'HTML',
  composeWindow: 'PAGE',
  encoding: 'UTF-8',
  draftAlert: false,
  senderName: '',
  replyTo: '',
  vcardAttach: false,
  vcard: null,
};

/** 유해태그 차단이 늘 지우는 것 — 화면이 도움말에 그대로 적는다. */
export const MAIL_UNSAFE_TAGS = ['html', 'head', 'meta', 'body', 'script', 'iframe', 'embed'] as const;

/** 인코딩 선택지 — 서버 PATCH 스키마의 enum 과 **같은 값**이어야 한다. */
export const MAIL_ENCODINGS: { id: MailEncoding; label: string }[] = [
  { id: 'UTF-8', label: '유니코드 (UTF-8)' },
  { id: 'EUC-KR', label: '한국어 (EUC-KR)' },
  { id: 'US-ASCII', label: '영어 (US-ASCII)' },
  { id: 'ISO-2022-JP', label: '일본어 (ISO-2022-JP)' },
  { id: 'GB2312', label: '중국어 간체 (GB2312)' },
  { id: 'BIG5', label: '중국어 번체 (BIG5)' },
];

/** 명함 글자 칸 하나. `labelKey` 는 mail-i18n 의 'mail.vcard.f.*' 다. */
export interface MailVcardField {
  id: Exclude<keyof MailVcard, 'photoUrl'>;
  labelKey: MailI18nKey;
  /** 한 줄을 통째로 쓰는 칸(주소·메모). */
  wide?: boolean;
}

/** 처음부터 서 있는 명함 칸 — 거의 다 쓰는 것만 (정본 P2-1 「항목 추가」). */
export const MAIL_VCARD_BASE: MailVcardField[] = [
  { id: 'name', labelKey: 'mail.vcard.f.name' },
  { id: 'org', labelKey: 'mail.vcard.f.org' },
  { id: 'email', labelKey: 'mail.vcard.f.email' },
  { id: 'mobile', labelKey: 'mail.vcard.f.mobile' },
  { id: 'officeTel', labelKey: 'mail.vcard.f.officeTel' },
  { id: 'officeAddr', labelKey: 'mail.vcard.f.officeAddr', wide: true },
  { id: 'memo', labelKey: 'mail.vcard.f.memo', wide: true },
];

/**
 * 「항목 추가」로 붙는 칸.
 * ★ id 는 서버 PATCH 스키마의 이름과 **같아야** 저장된다(additionalProperties:false).
 */
export const MAIL_VCARD_EXTRA: MailVcardField[] = [
  { id: 'homeTel', labelKey: 'mail.vcard.f.homeTel' },
  { id: 'homeAddr', labelKey: 'mail.vcard.f.homeAddr', wide: true },
  { id: 'messenger', labelKey: 'mail.vcard.f.messenger' },
  { id: 'homepage', labelKey: 'mail.vcard.f.homepage', wide: true },
];

/** 명함에서 «값이 든 칸»만 남긴다 — 빈 문자열을 저장하면 「비웠다」와 「안 적었다」가 같아진다. */
export function compactVcard(v: MailVcard | null): MailVcard | null {
  if (!v) return null;
  const out: MailVcard = {};
  for (const [k, val] of Object.entries(v)) {
    if (typeof val === 'string' && val.trim() !== '') (out as Record<string, unknown>)[k] = val.trim();
    else if (k === 'photoUrl' && typeof val === 'string' && val !== '') out.photoUrl = val;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** 서버 DTO → 화면 값. 서버가 모르는 키(구 서버)는 기본값으로 떨어진다. */
export function mailBasicFromDto(s: UserSettingsDto | null | undefined): MailBasicSettings {
  const d = MAIL_BASIC_DEFAULTS;
  return {
    // migration 110 의 넷 — 개인설정에 있던 것을 여기로 옮겼다(2026-09-06).
    firstFolder: (s?.mailFirstFolder as MailBasicSettings['firstFolder'] | undefined) ?? d.firstFolder,
    pageSize: (s?.mailPageSize as MailBasicSettings['pageSize'] | undefined) ?? d.pageSize,
    simplifySubject: s?.mailSimplifySubject ?? d.simplifySubject,
    quoteDivider: s?.mailQuoteDivider ?? d.quoteDivider,
    addrDisplay: s?.mailAddrDisplay ?? d.addrDisplay,
    blockImages: s?.mailBlockImages ?? d.blockImages,
    blockUnsafeTags: s?.mailBlockUnsafeTags ?? d.blockUnsafeTags,
    showOrigin: s?.mailShowOrigin ?? d.showOrigin,
    searchAll: s?.mailSearchAll ?? d.searchAll,
    listShowCc: s?.mailListShowCc ?? d.listShowCc,
    listDensity: s?.mailListDensity ?? d.listDensity,
    recipientInput: s?.mailRecipientInput ?? d.recipientInput,
    sendConfirm: s?.mailSendConfirm ?? d.sendConfirm,
    saveSent: s?.mailSaveSent ?? d.saveSent,
    readReceipt: s?.mailReadReceipt ?? d.readReceipt,
    composeMode: s?.mailComposeMode ?? d.composeMode,
    composeWindow: s?.mailComposeWindow ?? d.composeWindow,
    encoding: s?.mailEncoding ?? d.encoding,
    draftAlert: s?.mailDraftAlert ?? d.draftAlert,
    senderName: s?.mailSenderName ?? d.senderName,
    replyTo: s?.mailReplyTo ?? d.replyTo,
    vcardAttach: s?.mailVcardAttach ?? d.vcardAttach,
    vcard: s?.mailVcard ?? d.vcard,
  };
}

/**
 * 화면 값 → 서버로 보낼 것.
 *
 * ★ **바뀐 칸만 싣는다.** 통째로 보내면 다른 기기에서 방금 고친 값을 덮어쓴다.
 * ★ 빈 글자 칸은 `null` 로 보낸다 — 「비웠다」는 뜻이고, 서버가 계정 이름·보낸 주소를 쓴다.
 * ★ `blockUnsafeTags` 는 보내지 않는다 — 화면에서 바꿀 수 없는 값이다.
 */
export function mailBasicPatch(
  next: MailBasicSettings,
  base: MailBasicSettings,
): Partial<UserSettingsDto> {
  const p: Partial<UserSettingsDto> = {};
  if (next.firstFolder !== base.firstFolder) p.mailFirstFolder = next.firstFolder;
  if (next.pageSize !== base.pageSize) p.mailPageSize = next.pageSize;
  if (next.simplifySubject !== base.simplifySubject) p.mailSimplifySubject = next.simplifySubject;
  if (next.quoteDivider !== base.quoteDivider) p.mailQuoteDivider = next.quoteDivider;
  if (next.addrDisplay !== base.addrDisplay) p.mailAddrDisplay = next.addrDisplay;
  if (next.blockImages !== base.blockImages) p.mailBlockImages = next.blockImages;
  if (next.showOrigin !== base.showOrigin) p.mailShowOrigin = next.showOrigin;
  if (next.searchAll !== base.searchAll) p.mailSearchAll = next.searchAll;
  if (next.listShowCc !== base.listShowCc) p.mailListShowCc = next.listShowCc;
  if (next.listDensity !== base.listDensity) p.mailListDensity = next.listDensity;
  if (next.recipientInput !== base.recipientInput) p.mailRecipientInput = next.recipientInput;
  if (next.sendConfirm !== base.sendConfirm) p.mailSendConfirm = next.sendConfirm;
  if (next.saveSent !== base.saveSent) p.mailSaveSent = next.saveSent;
  if (next.readReceipt !== base.readReceipt) p.mailReadReceipt = next.readReceipt;
  if (next.composeMode !== base.composeMode) p.mailComposeMode = next.composeMode;
  if (next.composeWindow !== base.composeWindow) p.mailComposeWindow = next.composeWindow;
  if (next.encoding !== base.encoding) p.mailEncoding = next.encoding;
  if (next.draftAlert !== base.draftAlert) p.mailDraftAlert = next.draftAlert;
  if (next.senderName.trim() !== base.senderName.trim()) {
    p.mailSenderName = next.senderName.trim() === '' ? null : next.senderName.trim();
  }
  if (next.replyTo.trim() !== base.replyTo.trim()) {
    p.mailReplyTo = next.replyTo.trim() === '' ? null : next.replyTo.trim();
  }
  if (next.vcardAttach !== base.vcardAttach) p.mailVcardAttach = next.vcardAttach;
  const a = JSON.stringify(compactVcard(next.vcard) ?? null);
  const b = JSON.stringify(compactVcard(base.vcard) ?? null);
  if (a !== b) p.mailVcard = compactVcard(next.vcard);
  return p;
}

/** 고친 것이 있나 — 저장바가 이것으로 잠기고 풀린다. */
export function mailBasicDirty(next: MailBasicSettings, base: MailBasicSettings): boolean {
  return Object.keys(mailBasicPatch(next, base)).length > 0;
}
